/**
 * La projection de consommation du dashboard, sans une ligne de rendu.
 *
 * Le serveur rend des faits : ce qui est entré, a été consommé ou a péri
 * chaque mois, ce qui restait à l'ouverture de la fenêtre, et le mois courant
 * qui cale l'axe. Tout ce qui est une hypothèse d'affichage se calcule ici —
 * le rythme moyen, les mois projetés, le reste projeté, le mois d'épuisement
 * estimé — pour que le sélecteur de rythme réagisse sans aller-retour et que
 * le contrat ne promette jamais un « mois d'épuisement » que le grand livre ne
 * connaît pas.
 *
 * Deux règles absolues, héritées de `format/amount.ts` :
 *
 * - tout montant reste une chaîne de centièmes et se calcule en `BigInt` ;
 *   `ratio()` est le seul passage vers un `number`, pour une hauteur de barre ;
 * - le mois courant est celui du serveur (`to`), jamais celui du navigateur :
 *   le 1er du mois à 00h30 heure de Paris, l'écran afficherait sinon un mois
 *   d'avance sur les écritures.
 */

import type { Int64, MonthlyFlow, WalletConsumption } from "../api";
import { CENTIEMES_PAR_TOKEN, addAmounts, compareAmounts, fromBigInt, toBigInt } from "../format";
import { derniersMois, formatMois, moisCourant, moisPrecedent, parseMois } from "./feuilles/mois";
import type { Mois } from "./feuilles/mois";

/** Les profondeurs de moyenne proposées, en mois écoulés. */
export const RYTHMES = [3, 6, 12] as const;
export type Rythme = (typeof RYTHMES)[number];

/** Mois réels affichés, mois courant compris. */
export const MOIS_PASSES = 6;
/** Mois projetés affichés, à partir du mois suivant le courant. */
export const MOIS_FUTURS = 6;
/**
 * Ce que l'écran demande au serveur : les douze mois écoulés qu'exige la
 * moyenne la plus large, plus le mois en cours.
 */
export const MOIS_DEMANDES = Math.max(...RYTHMES) + 1;

/** Ce qu'un mois a vu passer, en centièmes. */
export type Flux = {
  entered: Int64;
  consumed: Int64;
  expired: Int64;
};

/** Une barre de l'histogramme et son point sur la courbe, réels ou projetés. */
export type Barre = {
  mois: Mois;
  /** « 2026-09 » : la clé React et la clé de la série. */
  cle: string;
  /** Consommé (réel) ou consommation projetée, en centièmes. */
  montant: Int64;
  /**
   * Somme des barres de même nature jusqu'à celle-ci : depuis la première
   * barre réelle affichée pour le passé, depuis le mois courant pour le futur.
   */
  cumul: Int64;
  /**
   * Le reste à consommer en fin de mois, en centièmes : reconstitué sur les
   * écritures pour le passé, projeté pour le futur — jamais négatif dans ce
   * second cas, on ne consomme pas ce qu'on n'a pas.
   */
  reste: Int64;
  nature: "reel" | "projete";
  /** Vrai pour le mois courant, celui qui se remplit encore. */
  courant: boolean;
};

/** Ce que la projection dit de la fin du carburant. */
export type Epuisement =
  | { type: "mois"; mois: Mois }
  | { type: "au-dela" }
  | { type: "deja-epuise" }
  | { type: "aucune-consommation" };

export type Projection = {
  /** `MOIS_PASSES` barres réelles, du plus ancien au mois courant. */
  passees: Barre[];
  /** `MOIS_FUTURS` barres projetées, du mois suivant au plus lointain. */
  futures: Barre[];
  /** Consommation moyenne par mois écoulé, en centièmes, arrondie à l'entier inférieur. */
  moyenne: Int64;
  /** Ce qui reste à consommer aujourd'hui, en centièmes : le reste du mois courant. */
  carburant: Int64;
  epuisement: Epuisement;
  /** Le haut de l'axe des barres, en centièmes : un palier rond au-dessus de la plus haute. */
  echelleMax: Int64;
  /** Les graduations de l'axe des barres, de zéro à `echelleMax` inclus. */
  graduations: Int64[];
  /** Le haut de l'axe de la courbe de reste, en centièmes. */
  echelleReste: Int64;
  /** Les graduations de l'axe de la courbe. */
  graduationsReste: Int64[];
};

const ZERO: Int64 = "0";
const FLUX_NUL: Flux = { entered: ZERO, consumed: ZERO, expired: ZERO };

/** Les abréviations usuelles, celles d'un agenda français. */
const ABREGES_MOIS = [
  "janv.",
  "févr.",
  "mars",
  "avr.",
  "mai",
  "juin",
  "juil.",
  "août",
  "sept.",
  "oct.",
  "nov.",
  "déc.",
];

/** « sept. » : l'étiquette d'axe, qui n'a pas la place du mois entier. */
export function abregeMois(m: Mois): string {
  return ABREGES_MOIS[m.mois - 1];
}

/** Le mois d'après. `mois.ts` ne connaît que le précédent : il ne regarde jamais devant. */
export function moisSuivant(m: Mois): Mois {
  return m.mois === 12 ? { annee: m.annee + 1, mois: 1 } : { annee: m.annee, mois: m.mois + 1 };
}

/** Les mois de `debut` à `fin` inclus, dans l'ordre. Vide si `fin` précède `debut`. */
export function moisEntre(debut: Mois, fin: Mois): Mois[] {
  const out: Mois[] = [];
  let m = debut;
  const cleFin = formatMois(fin);
  while (formatMois(m) <= cleFin) {
    out.push(m);
    m = moisSuivant(m);
  }
  return out;
}

/**
 * La série reçue, indexée par clé de mois, les mois de la fenêtre absents de
 * la réponse comptant zéro partout. Le serveur ne rend que les mois qui ont au
 * moins une écriture ; c'est ici que les trous se comblent.
 */
export function serieComplete(
  mois: readonly MonthlyFlow[],
  fenetre: readonly Mois[],
): Map<string, Flux> {
  const serie = new Map<string, Flux>();
  for (const m of fenetre) {
    serie.set(formatMois(m), FLUX_NUL);
  }
  for (const m of mois) {
    serie.set(m.month, { entered: m.entered, consumed: m.consumed, expired: m.expired });
  }
  return serie;
}

/**
 * Moyenne de la consommation des `n` mois qui se terminent à `dernier` inclus,
 * en remontant.
 *
 * Un mois absent de la série compte zéro, et non « inconnu » : un portefeuille
 * de quatre mois d'âge n'a rien consommé les huit mois d'avant, et c'est bien
 * ce que « moyenne des douze derniers mois » veut dire. La division est
 * entière, à l'inférieur : un centième de token de trop projeté sur six mois
 * ne vaut pas un flottant.
 */
export function moyenneSur(serie: ReadonlyMap<string, Flux>, dernier: Mois, n: number): Int64 {
  if (n <= 0) {
    return ZERO;
  }
  let total = 0n;
  for (const m of derniersMois(n, dernier)) {
    total += toBigInt((serie.get(formatMois(m)) ?? FLUX_NUL).consumed);
  }
  return fromBigInt(total / BigInt(n));
}

/**
 * Le reste à consommer en fin de chaque mois de `fenetre`, à partir du reste
 * d'ouverture : chaque mois ajoute son entré et retire son consommé et son
 * périmé. C'est la reconstitution exacte de « disponible + réservé » à la fin
 * de chaque mois, puisque réservations et relâches ne changent pas cette somme.
 */
export function restesSur(
  serie: ReadonlyMap<string, Flux>,
  fenetre: readonly Mois[],
  ouverture: Int64,
): Map<string, Int64> {
  const restes = new Map<string, Int64>();
  let reste = toBigInt(ouverture);
  for (const m of fenetre) {
    const cle = formatMois(m);
    const f = serie.get(cle) ?? FLUX_NUL;
    reste += toBigInt(f.entered) - toBigInt(f.consumed) - toBigInt(f.expired);
    restes.set(cle, fromBigInt(reste));
  }
  return restes;
}

/**
 * Le palier rond immédiatement au-dessus de `max` : 1, 2 ou 5 tokens fois une
 * puissance de dix, jamais moins d'un token. C'est le haut de l'axe ; une
 * échelle qui s'arrête pile sur la plus haute barre ne laisse pas d'air à
 * l'étiquette.
 */
export function palierEchelle(max: Int64): Int64 {
  const cible = toBigInt(max);
  let base = CENTIEMES_PAR_TOKEN;
  for (;;) {
    for (const facteur of [1n, 2n, 5n]) {
      const palier = base * facteur;
      if (palier >= cible) {
        return fromBigInt(palier);
      }
    }
    base *= 10n;
  }
}

/**
 * Les graduations d'un axe qui monte à `palier` : quatre pas quand le palier
 * commence par 1 ou 2, cinq quand il commence par 5. C'est ce qui garde des
 * étiquettes rondes — 0, 500, 1 000, 1 500, 2 000 — plutôt que des quarts.
 */
export function graduations(palier: Int64): Int64[] {
  const haut = toBigInt(palier);
  if (haut <= 0n) {
    return [ZERO];
  }
  const premierChiffre = Number(haut.toString(10)[0]);
  const pas = premierChiffre === 5 ? 5n : 4n;
  const out: Int64[] = [];
  for (let i = 0n; i <= pas; i++) {
    out.push(fromBigInt((haut * i) / pas));
  }
  return out;
}

/**
 * Le tracé d'une barre : coins arrondis en haut, carrés à la base, comme une
 * colonne qui pousse depuis l'axe. Le rayon se réduit si la barre est plus
 * basse ou plus étroite que lui, pour qu'une barre minuscule reste un
 * rectangle et non un artefact. Une hauteur nulle ne trace rien.
 */
export function cheminBarre(
  x: number,
  y: number,
  largeur: number,
  hauteur: number,
  rayon: number,
): string {
  if (hauteur <= 0 || largeur <= 0) {
    return "";
  }
  const r = Math.max(0, Math.min(rayon, hauteur, largeur / 2));
  const bas = y + hauteur;
  const droite = x + largeur;
  return [
    `M${x},${bas}`,
    `V${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `H${droite - r}`,
    `Q${droite},${y} ${droite},${y + r}`,
    `V${bas}`,
    "Z",
  ].join(" ");
}

export type Point = { x: number; y: number };

/**
 * Le tracé d'une ligne brisée par ses points. Des segments droits, pas une
 * spline : une courbe lissée inventerait entre deux mois des valeurs que
 * personne n'a mesurées. Moins de deux points ne tracent rien.
 */
export function cheminCourbe(points: readonly Point[]): string {
  if (points.length < 2) {
    return "";
  }
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
}

const FORMAT_ENTIER = new Intl.NumberFormat("fr-FR", {
  useGrouping: true,
  maximumFractionDigits: 0,
});

/**
 * Une étiquette d'axe, courte : des tokens entiers (« 1 250 »), des décimales
 * seulement si elles ne sont pas nulles (« 0,25 »), et des milliers abrégés
 * au-delà de dix mille (« 12,5 k »). L'axe n'a pas la place de `formatTokens`
 * et son « 12 500,00 » : c'est l'infobulle et le tableau qui portent le montant
 * exact.
 */
export function formatTokensAxe(valeur: Int64): string {
  const centiemes = toBigInt(valeur);
  const tokens = centiemes / CENTIEMES_PAR_TOKEN;
  const reste = centiemes % CENTIEMES_PAR_TOKEN;
  if (tokens >= 10_000n) {
    const milliers = tokens / 1000n;
    const dixieme = (tokens % 1000n) / 100n;
    return dixieme === 0n
      ? `${FORMAT_ENTIER.format(milliers)} k`
      : `${FORMAT_ENTIER.format(milliers)},${dixieme} k`;
  }
  const entier = FORMAT_ENTIER.format(tokens);
  if (reste === 0n) {
    return entier;
  }
  const decimales = reste.toString(10).padStart(2, "0").replace(/0$/, "");
  return `${entier},${decimales}`;
}

function plusGrand(a: Int64, b: Int64): Int64 {
  return compareAmounts(a, b) >= 0 ? a : b;
}

/**
 * La projection complète, à partir de la série du serveur et du rythme choisi.
 *
 * Le mois courant, incomplet, est affiché tel quel dans le passé mais exclu de
 * la moyenne : à mi-mois il tirerait le rythme vers le bas et repousserait
 * l'épuisement d'autant. Chaque mois projeté porte la moyenne entière — on
 * montre un rythme, pas le reliquat — et c'est le reste qui s'arrête à zéro.
 *
 * Le carburant est le reste reconstitué à la fin du mois courant, c'est-à-dire
 * aujourd'hui : c'est aussi « disponible + réservé » de la balance, calculé sur
 * les mêmes écritures. Le prendre ici plutôt que dans la balance garantit que
 * la courbe est continue entre son dernier point réel et son premier point
 * projeté.
 */
export function projeter(p: { consommation: WalletConsumption; rythme: Rythme }): Projection {
  // `to` et `from` sont obligatoires au contrat ; le repli sur l'horloge locale
  // ne sert qu'à ne pas casser l'écran sur une réponse malformée.
  const courant = parseMois(p.consommation.to) ?? moisCourant();
  const debut =
    parseMois(p.consommation.from) ?? derniersMois(MOIS_DEMANDES, courant)[MOIS_DEMANDES - 1];
  const dernierEcoule = moisPrecedent(courant);

  const fenetre = moisEntre(debut, courant);
  const fenetrePassee = derniersMois(MOIS_PASSES, courant).reverse();
  const fenetreMoyenne = derniersMois(Math.max(...RYTHMES), dernierEcoule);
  const serie = serieComplete(p.consommation.months, [
    ...fenetre,
    ...fenetrePassee,
    ...fenetreMoyenne,
  ]);
  const restes = restesSur(serie, fenetre, p.consommation.opening);

  const moyenne = moyenneSur(serie, dernierEcoule, p.rythme);
  const carburant = plusGrand(restes.get(formatMois(courant)) ?? ZERO, ZERO);

  let cumul = ZERO;
  const passees: Barre[] = fenetrePassee.map((mois) => {
    const cle = formatMois(mois);
    const montant = (serie.get(cle) ?? FLUX_NUL).consumed;
    cumul = addAmounts(cumul, montant);
    return {
      mois,
      cle,
      montant,
      cumul,
      // Un mois d'avant la fenêtre n'a pas de reste connu : on repart de
      // l'ouverture, ce qui ne se produit que si la fenêtre est plus courte
      // que les six mois affichés.
      reste: restes.get(cle) ?? p.consommation.opening,
      nature: "reel",
      courant: cle === formatMois(courant),
    };
  });

  const moyenneNulle = toBigInt(moyenne) === 0n;
  const carburantNul = toBigInt(carburant) === 0n;
  let epuisement: Epuisement = moyenneNulle
    ? { type: "aucune-consommation" }
    : carburantNul
      ? { type: "deja-epuise" }
      : { type: "au-dela" };

  const futures: Barre[] = [];
  let mois = courant;
  let cumulProjete = 0n;
  const reserve = toBigInt(carburant);
  for (let i = 0; i < MOIS_FUTURS; i++) {
    mois = moisSuivant(mois);
    cumulProjete += toBigInt(moyenne);
    const reste = reserve - cumulProjete;
    if (epuisement.type === "au-dela" && reste <= 0n) {
      epuisement = { type: "mois", mois };
    }
    futures.push({
      mois,
      cle: formatMois(mois),
      montant: moyenne,
      cumul: fromBigInt(cumulProjete),
      reste: fromBigInt(reste > 0n ? reste : 0n),
      nature: "projete",
      courant: false,
    });
  }

  const toutes = [...passees, ...futures];
  const echelleMax = palierEchelle(toutes.map((b) => b.montant).reduce(plusGrand, ZERO));
  const echelleReste = palierEchelle(toutes.map((b) => b.reste).reduce(plusGrand, ZERO));

  return {
    passees,
    futures,
    moyenne,
    carburant,
    epuisement,
    echelleMax,
    graduations: graduations(echelleMax),
    echelleReste,
    graduationsReste: graduations(echelleReste),
  };
}
